import React from "react";
import styled from "styled-components";
import { BLOCKS, MARKS, INLINES } from "@contentful/rich-text-types";
import { renderRichText } from "gatsby-source-contentful/rich-text";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer";
import { ModulePrimaryBtn } from "storybook-components/Button";
import {
  Body,
  H3,
  H4,
  H5,
  SubheadSm,
  SubheadLg,
  BodySemibold
} from "storybook-components/Typography";
import { GreenLink } from "storybook-components/Link";
import { FlexContainer } from "storybook-components/Container";
import ResponsiveYoutubeVideo from "./ResponsiveYoutubeVideo";
import BlogPostCardLink from "./BlogPostCardLink";
import CodeBlock from "./CodeBlock";
import RichTextBlockLink from "./RichTextBlockLink";
import QuotationMarks from "./images/quotation-marks.svg";
import { formatIdForAnchorLink } from "../Helpers/AnchorLinkFormatter";

const CodeBackgroundColor = "#282C34";

const BlogPostStyleWrapper = styled.div`
  max-width: 970px;
  line-height: 1.7;
`;

const PostGif = styled.img`
  width: 100%;
  object-fit: cover;
`;

const PullquoteWrapper = styled.div`
  display: flex;
  margin: 4rem auto;
  padding: ${props =>
    props.showQuoteMarks ? "16px 88px 16px 0" : "16px 88px;"};
`;

const StyledQuoteMarks = styled.img`
  margin: 0 24px 0 0;
  width: 64px;
  height: 47px;
`;

const LongformH1 = styled(H3)`
  margin: 4.5rem 0 2rem;
  scroll-margin-top: 90px;
  ${({ theme }) => theme.media.tabletUp`
    line-height: 115%;
    font-size: 2.25rem;
  `};
`;

const LongformH2 = styled(H4)`
  margin: 3rem 0 1.5rem;
  scroll-margin-top: 90px;
  ${({ theme }) => theme.media.tabletUp`
    line-height: 120%;
    font-size: 1.75rem;
  `};
`;

const LongformH3 = styled(H5)`
  margin: 6.5rem 0 1rem;
  scroll-margin-top: 90px;
  ${({ theme }) => theme.media.tabletUp`
    line-height: 125%;
    font-size: 1.5rem;
  `};
`;

const StyledSubhead = styled(SubheadSm)`
  margin: 2.25rem 0 1rem;
  scroll-margin-top: 90px;
`;

const StyledPullquote = styled(SubheadLg)`
  margin-top: 0;
  margin-bottom: 0;
  color: ${({ theme }) => theme.colors.green5};
`;

const StyledPullquoteByline = styled(BodySemibold)`
  margin-top: 1rem;
  color: ${({ theme }) => theme.colors.green5};
`;

const StyledLink = styled(GreenLink)`
  margin-left: 0;
  color: ${({ theme }) => theme.colors.green4};
  &:hover {
    color: ${({ theme }) => theme.colors.green4};
    text-decoration: underline;
  }
`;

const StyledBlockLink = styled.a`
  display: block;
  margin: 1rem auto;
`;

const StyledBodyWithWhitespace = styled(Body)`
  white-space: pre-wrap;
  .bold {
    font-weight: 500;
  }
`;

const StyledInlineCode = styled.code`
  background: ${CodeBackgroundColor};
  color: ${({ theme }) => theme.colors.gray200};
  border-radius: 2px;
  padding: 2px;
`;

const Bold = ({ children }) => <span className="bold">{children}</span>;
const options = {
  renderMark: {
    [MARKS.BOLD]: text => <Bold>{text}</Bold>,
    [MARKS.CODE]: text => <StyledInlineCode>{text}</StyledInlineCode>
  },
  renderNode: {
    [BLOCKS.HEADING_1]: (node, children) => (
      <LongformH1 id={formatIdForAnchorLink(documentToPlainTextString(node))}>
        {children}
      </LongformH1>
    ),
    [BLOCKS.HEADING_2]: (node, children) => (
      <LongformH2 id={formatIdForAnchorLink(documentToPlainTextString(node))}>
        {children}
      </LongformH2>
    ),
    [BLOCKS.HEADING_3]: (node, children) => (
      <LongformH3 id={formatIdForAnchorLink(documentToPlainTextString(node))}>
        {children}
      </LongformH3>
    ),
    [BLOCKS.HEADING_4]: (node, children) => (
      <StyledSubhead
        id={formatIdForAnchorLink(documentToPlainTextString(node))}
      >
        {children}
      </StyledSubhead>
    ),
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <StyledBodyWithWhitespace>{children}</StyledBodyWithWhitespace>
    ),
    [BLOCKS.QUOTE]: (node, children) => (
      <StyledPullquote>{children}</StyledPullquote>
    ),
    /* eslint-disable no-underscore-dangle */
    [BLOCKS.EMBEDDED_ASSET]: ({ data }) => {
      const image = getImage(data.target);
      // gatsby-plugin-image doesn't support gifs, making this workaround to accomodate them
      // Gatsby docs suggest using <img> for gifs - see - https://www.gatsbyjs.com/docs/how-to/images-and-media/working-with-gifs/
      const isGif = data.target?.file?.contentType === "image/gif";
      return (
        <>
          {isGif ? (
            <PostGif src={data.target?.file?.url} />
          ) : (
            <GatsbyImage
              image={image}
              alt={data.target && data.target.title}
              objectFit="contain"
            />
          )}
        </>
      );
    },
    [BLOCKS.EMBEDDED_ENTRY]: ({ data }) => {
      if (data.target && data.target.__typename === "ContentfulYoutubeVideo") {
        return <ResponsiveYoutubeVideo src={`${data.target.embedUrl}`} />;
      }
      if (data.target && data.target.__typename === "ContentfulCodeBlock") {
        return <CodeBlock>{data.target.code.code}</CodeBlock>;
      }
      if (data.target && data.target.__typename === "ContentfulBlockQuote") {
        const showQuoteMarks = data.target.byline;
        return (
          <PullquoteWrapper showQuoteMarks={showQuoteMarks}>
            {showQuoteMarks && <StyledQuoteMarks src={QuotationMarks} />}
            <div>
              <StyledPullquote>{data.target.quote.quote}</StyledPullquote>
              <StyledPullquoteByline>
                {data.target.byline}
              </StyledPullquoteByline>
            </div>
          </PullquoteWrapper>
        );
      }
      if (data.target && data.target.__typename === "ContentfulCtaButton") {
        return (
          <FlexContainer>
            <StyledBlockLink href={data.target.linkUrl} target="_blank">
              <ModulePrimaryBtn>{data.target.buttonText}</ModulePrimaryBtn>
            </StyledBlockLink>
          </FlexContainer>
        );
      }
      if (data.target && data.target.__typename === "ContentfulCtaLink") {
        return (
          <RichTextBlockLink
            linkUrl={data.target.linkUrl}
            linkText={data.target.linkText}
          />
        );
      }
      if (
        data.target &&
        data.target.__typename === "ContentfulContentLinkCard"
      ) {
        return (
          <BlogPostCardLink
            cta={data.target.cta}
            url={data.target.url}
            title={data.target.title}
            description={data.target.description}
            imageData={data.target.image}
            buttonText={data.target.buttonText}
          />
        );
      }
      return "";
    },
    [INLINES.ENTRY_HYPERLINK]: ({ node }, children) => (
      <GreenLink href={`/pages/${node.data.target.slug}`} target="_blank">
        {children}
      </GreenLink>
    ),
    [INLINES.HYPERLINK]: (node, children) => {
      const { data } = node;
      return data.uri.match("/embed$") ? (
        <a className="mode-embed" href={data.uri}>
          {children}
        </a>
      ) : (
        <StyledLink href={data.uri} target="_blank">
          {children}
        </StyledLink>
      );
    }
  }
};

const BlogPostRichTextContent = ({ richTextContent }) => (
  <BlogPostStyleWrapper>
    {renderRichText(richTextContent, options)}
  </BlogPostStyleWrapper>
);

export default BlogPostRichTextContent;
